import { NodeModel, PortModelAlignment } from "@projectstorm/react-diagrams";
import _ from "lodash";
import { NaryRelationPortModel } from "./NaryRelationPortModel";
import { ADDPORT, FUNCTION, PREDICATE } from "./ConstantNames";
import { functionIsAlreadyDefinedForGivenFunction } from "./functions";
export class BaseNodeModel extends NodeModel {
  constructor(options = {}) {
    super({ ...options
    });
    this.changeCounter = 0;
    this.predicates = new Set();
    this.functions = new Set();
    this.parameterPorts = new Map();
    this.parameterPortsArray = new Array(this.getNumberOfPorts());
    this.editable = this.getReduxProps()["editable"];
    this.registerEvents();
    this.registerParameterPorts();
    this.registerAppendPort();
  }

  registerParameterPorts() {
    let directions = [PortModelAlignment.LEFT, PortModelAlignment.RIGHT, PortModelAlignment.TOP, PortModelAlignment.BOTTOM];

    for (let i = 0; i < this.getNumberOfPorts(); i++) {
      let port = this.addPort(new NaryRelationPortModel("port" + i, directions[i]));
      this.parameterPorts.set(port, null);
      this.parameterPortsArray[i] = port;
    }
  }

  getNodeNameCombination() {
    let value = [];

    for (let i = 0; i < this.parameterPortsArray.length; i++) {
      let portValue = this.parameterPorts.get(this.parameterPortsArray[i]);

      if (!portValue) {
        return null;
      }

      value.push(portValue.getNodeName());
    }

    return value;
  }

  getNodeParameters() {
    let value = [];

    for (let i = 0; i < this.parameterPortsArray.length; i++) {
      let portValue = this.parameterPorts.get(this.parameterPortsArray[i]);

      if (!portValue) {
        return null;
      }

      if (i !== this.parameterPortsArray.length - 1) {
        value.push(portValue.getNodeName());
      }
    }

    return value;
  }

  getFunctionValue() {
    if (this.getNodeNameCombination()) {
      return this.parameterPorts.get(this.parameterPortsArray[this.parameterPortsArray.length - 1]).getNodeName();
    }

    return null;
  }

  getValueOfPort(port) {
    if (this.parameterPorts.has(port)) {
      return this.parameterPorts.get(port);
    }

    return null;
  }

  getValueNameOfPort(port) {
    if (this.parameterPorts.has(port)) {
      return this.parameterPorts.get(port).getNodeName();
    }

    return null;
  }

  removeValueFromPort(port) {
    this.parameterPorts.set(port, null);
  }

  setValueToPort(port, valueNode) {
    this.parameterPorts.set(port, valueNode);
  }

  increaseChangeCounter() {
    this.changeCounter += 1;
  }

  getNumberOfPorts() {
    return this.getOptions().numberOfPorts;
  }

  clearPredicates() {
    this.predicates = new Set();
    this.increaseChangeCounter();
  }

  clearFunctions() {
    this.functions = new Set();
    this.increaseChangeCounter();
  }

  getPredicates() {
    return this.predicates;
  }

  getFunctions() {
    return this.functions;
  }

  removePort(port) {
    for (let link of _.values(port.getLinks())) {
      link.remove();
    } //clear the parent node reference


    if (this.ports[port.getName()]) {
      this.ports[port.getName()].setParent(null);
      delete this.ports[port.getName()];
    }
  }

  serialize() {
    return { ...super.serialize(),
      changeCounter: this.changeCounter,
      editable: this.editable
    };
  }

  getPortByIndex(index) {
    if (index < 0 || index >= this.getNumberOfPorts()) {
      return null;
    }

    return this.parameterPortsArray[index];
  }

  deserialize(event) {
    super.deserialize(event);
    this.changeCounter = event.date.changeCounter;
    this.editable = event.data.editable;
  }

  registerEvents() {
    let node = this;
    this.registerListener({
      entityRemoved() {
        node.removeNodeFromMathView();
        node.removeNodeFromGraphView();
      }

    });
  }

  removeNodeFromGraphView() {
    throw new Error("This method should be implemented in child");
  }

  changeEditableState(value) {
    this.editable = value;
  }

  getNodeName() {
    return this.getOptions().name;
  }

  getAppendPort() {
    return this.appendPort;
  }

  renameNode(name) {
    this.getOptions().name = name;
  }

  isEditable() {
    return this.editable;
  }

  addPredicateToSet(name) {
    this.predicates.add(name);
    this.increaseChangeCounter();
  }

  addFunctionToSet(name) {
    this.functions.add(name);
    this.increaseChangeCounter();
  }

  removePredicateFromSet(name) {
    this.predicates.delete(name);
    this.increaseChangeCounter();
  }

  removeFunctionFromSet(name) {
    this.functions.delete(name);
    this.increaseChangeCounter();
  }

  addPredicate(name) {
    name = name.replace(/\s/g, "");

    if (!this.predicates.has(name)) {
      this.addPredicateToSet(name);
      this.addElementToMathView(name, PREDICATE);
    }
  }

  addFunction(name) {
    name = name.replace(/\s/g, "");

    if (!this.functions.has(name)) {
      this.addFunctionToSet(name);
      this.addElementToMathView(name, FUNCTION);
    }
  }

  removePredicate(name) {
    if (this.predicates.has(name)) {
      this.removeElementFromMathView(name, PREDICATE);
    }

    this.removePredicateFromSet(name);
  }

  removeFunction(name) {
    if (this.functions.has(name)) {
      this.removeElementFromMathView(name, FUNCTION);
    }

    this.removeFunctionFromSet(name);
  }

  removeNodeFromMathView() {
    for (let predicate of this.getPredicates()) {
      this.removeElementFromMathView(predicate, PREDICATE);
    }

    for (let func of this.getFunctions()) {
      this.removeElementFromMathView(func, FUNCTION);
    }
  }

  representNodeInMathView() {
    for (let predicate of this.getPredicates()) {
      this.addElementToMathView(predicate, PREDICATE);
    }

    let functionsToDelete = new Set();

    for (let func of this.getFunctions()) {
      if (!functionIsAlreadyDefinedForGivenFunction(this.getNodeParameters(), this.getFunctionValue(), func + "/" + (this.getNumberOfPorts() - 1).toString(), this.getReduxProps())) {
        this.addElementToMathView(func, FUNCTION);
      } else {
        functionsToDelete.add(func);
      }
    }

    for (let func of functionsToDelete) {
      this.removeFunctionFromSet(func);
    }
  }

  addElementToMathView(name, type) {
    throw new Error("This method should be implemented in child");
  }

  removeElementFromMathView(name, type) {
    throw new Error("This method should be implemented in child");
  }

  getReduxProps() {
    return this.getOptions().reduxProps;
  }

  registerAppendPort() {
    let port = new NaryRelationPortModel(ADDPORT);
    port.setMaximumLinks(0);
    this.addPort(port);
    this.appendPort = port;
  }

}